import React, { useContext, useState } from "react";
import _ from "lodash";
import {
    Alert,
    Space,
    PageHeader,
    Table,
    Empty,
    Segmented,
    Row,
    Col,
    Dropdown,
    Menu,
    Button,
    Modal,
    Tooltip,
} from "antd";
import {
    MoreOutlined,
    HeartOutlined,
    HeartFilled,
    DeleteOutlined,
} from "@ant-design/icons";

import { GlobalContext } from "../GlobalContext";
import LanguageSwitcher from "../LanguageSwitcher";

import { getSongUniqueIdBySongID } from "../SongData";
import { columns } from "./columns";

const ScoresEnso = () => {
    const { language, userData, setProfile, setRecordEnso } =
        useContext(GlobalContext);
    const [currentPlayer, setCurrentPlayer] = useState(1);

    let scoreData, userProfileData;

    if (currentPlayer === 1) {
        userProfileData = userData.profile_p1;
        scoreData = userData.record_enso_p1;
    } else if (currentPlayer === 2) {
        userProfileData = userData.profile_p2;
        scoreData = userData.record_enso_p2;
    } else {
        scoreData = [];
    }

    const removeScore = (record) => {
        Modal.confirm({
            title: "Are you sure you want to delete this score?",
            content: "This action cannot be undone.",
            okText: "Delete",
            cancelText: "Cancel",
            onOk: () => {
                let newScoreData = _.remove(scoreData, (item) => {
                    return item.song_id !== record.song_id;
                });

                setRecordEnso(newScoreData, currentPlayer);
            },
        });
    };

    const toggleFavorites = (song_id) => {
        let uniqueId = getSongUniqueIdBySongID(song_id);

        if (!userProfileData.userFavorites) {
            userProfileData.userFavorites = [];
        }

        if (userProfileData.userFavorites.includes(uniqueId)) {
            userProfileData.userFavorites = _.remove(
                userProfileData.userFavorites,
                (item) => {
                    return item !== uniqueId;
                }
            );
        } else {
            userProfileData.userFavorites.push(uniqueId);
        }

        setProfile(userProfileData, currentPlayer);
    };

    let menuColumns = [
        {
            title: <HeartOutlined />,
            dataIndex: "favorites",
            align: "center",
            key: "favorites",
            fixed: "right",
            width: "50px",
            render: (text, record) => {
                if (!userProfileData.userFavorites) {
                    userProfileData.userFavorites = [];
                }

                let uniqueId = getSongUniqueIdBySongID(record.song_id);
                let isFavorited =
                    userProfileData.userFavorites.includes(uniqueId);

                return (
                    <Button
                        type="text"
                        size="small"
                        onClick={() => toggleFavorites(record.song_id)}
                    >
                        <Tooltip
                            title={
                                isFavorited
                                    ? "Remove from favorites"
                                    : "Add to favorites"
                            }
                        >
                            {isFavorited ? <HeartFilled /> : <HeartOutlined />}
                        </Tooltip>
                    </Button>
                );
            },
            sorter: (a, b) => {
                if (!userProfileData.userFavorites) {
                    userProfileData.userFavorites = [];
                }

                let uniqueId = getSongUniqueIdBySongID(a.song_id);
                let isFavorited =
                    userProfileData.userFavorites.includes(uniqueId);

                if (isFavorited) {
                    return -1;
                } else {
                    return 1;
                }
            },
        },
        {
            title: "",
            scoreDataIndex: "more_menu",
            key: "more_menu",
            width: "50px",
            fixed: "right",
            align: "center",
            render: (text, record) => {
                const menu = () => {
                    return (
                        <Menu
                            items={[
                                {
                                    label: "Delete score",
                                    key: "2",
                                    icon: <DeleteOutlined />,
                                    danger: true,
                                    onClick: () => {
                                        removeScore(record);
                                    },
                                },
                            ]}
                        />
                    );
                };

                return (
                    <Space size="middle">
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button size="small" type="text">
                                <MoreOutlined />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    let tableColumns = [...columns(language), ...menuColumns];

    return (
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <PageHeader
                className="site-page-header"
                title="High Scores — Taiko Mode"
            />

            {scoreData.length > 0 ? (
                <Row style={{ padding: "0 24px" }}>
                    <Alert
                        message="Adding songs to your favorites with this tool will only work with Omnimix v7 or higher installed."
                        type="warning"
                        showIcon
                        closable
                        style={{
                            width: "100%",
                        }}
                    />
                </Row>
            ) : null}

            <Row style={{ padding: "0 24px" }}>
                <Col span={12}>
                    <Segmented
                        value={currentPlayer}
                        onChange={(value) => setCurrentPlayer(value)}
                        options={[
                            {
                                label: "Player 1",
                                value: 1,
                            },
                            {
                                label: "Player 2",
                                value: 2,
                            },
                        ]}
                        style={{
                            marginBottom: "24px",
                        }}
                    />
                </Col>
                <Col span={12} align="right">
                    <LanguageSwitcher />
                </Col>
            </Row>

            <Row style={{ padding: "0 24px" }}>
                {userData && scoreData?.length ? (
                    <Table
                        columns={tableColumns}
                        dataSource={scoreData}
                        pagination={{ position: "topRight" }}
                        size="small"
                        scroll={{ x: 1100 }}
                        sticky={{
                            offsetHeader: 0,
                        }}
                    />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No score data. Go play some songs!"
                        style={{
                            marginTop: "50px",
                            display: `block`,
                            width: `100%`,
                        }}
                    />
                )}
            </Row>
        </Space>
    );
};

export default ScoresEnso;
